import React, { useState } from 'react';
import { auth } from '../firebase';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { useAuth } from '../context/AuthContext';
import { trackLogin } from '../services/analytics';

const css = `
  @keyframes login-rise {
    from { opacity: 0; transform: translateY(30px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes login-drift {
    0%,100% { transform: translate(0,0); }
    50%     { transform: translate(14px,-18px); }
  }
  @keyframes login-twinkle {
    0%,100% { opacity: 0.15; transform: scale(0.8); }
    50%     { opacity: 0.8;  transform: scale(1.2); }
  }
  @keyframes login-shine {
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  @keyframes loginSpin { to { transform: rotate(360deg); } }

  .login-page {
    position: relative;
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #dbeeff;
    overflow: hidden;
    padding: 20px;
    box-sizing: border-box;
  }
  .login-page::before {
    content: '';
    position: absolute; inset: 0;
    background:
      radial-gradient(ellipse 80% 50% at 20% 0%, rgba(56,132,255,0.18), transparent 60%),
      radial-gradient(ellipse 60% 50% at 80% 100%, rgba(12,35,64,0.10), transparent 60%),
      linear-gradient(180deg, #e6f3ff 0%, #c9e3ff 100%);
    pointer-events: none;
  }
  .login-page::after {
    content: '';
    position: absolute; inset: 0;
    background-image:
      linear-gradient(rgba(12,35,64,0.06) 1px, transparent 1px),
      linear-gradient(90deg, rgba(12,35,64,0.06) 1px, transparent 1px);
    background-size: 60px 60px;
    mask-image: radial-gradient(ellipse at top, black 30%, transparent 80%);
    pointer-events: none;
  }

  .login-stars {
    position: absolute; inset: 0;
    pointer-events: none; z-index: 1;
  }
  .login-stars span {
    position: absolute;
    width: 3px; height: 3px;
    border-radius: 50%;
    background: #1e3a8a;
    box-shadow: 0 0 10px rgba(30,58,138,0.6);
    animation: login-twinkle 3.5s ease-in-out infinite;
  }
  .login-orb {
    position: absolute;
    width: 380px; height: 380px; border-radius: 50%;
    background: radial-gradient(circle, rgba(59,130,246,0.35), transparent 70%);
    filter: blur(60px);
    pointer-events: none;
    animation: login-drift 14s ease-in-out infinite;
    z-index: 1;
  }
  .login-orb.a { top: -120px; left: -100px; }
  .login-orb.b { bottom: -160px; right: -120px; background: radial-gradient(circle, rgba(147,197,253,0.55), transparent 70%); animation-duration: 18s; }

  .login-card {
    position: relative; z-index: 3;
    background: linear-gradient(160deg, #ffffff 0%, #eaf4ff 100%);
    border: 1px solid rgba(12,35,64,0.18);
    border-radius: 20px;
    box-shadow:
      0 6px 32px rgba(12,35,64,0.13),
      0 0 0 1px rgba(255,255,255,0.6) inset;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    width: 100%;
    max-width: 860px;
    overflow: hidden;
    animation: login-rise 0.7s ease both;
  }
  .login-left {
    flex: 0 0 auto;
    width: 360px;
    overflow: hidden;
  }
  .login-left img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    object-position: top center;
  }
  .login-right {
    flex: 1;
    padding: 52px 44px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 24px;
    min-width: 0;
  }
  .login-mobile-header { display: none; }
  .login-mobile-brand {
    font-family: 'DM Sans', -apple-system, sans-serif;
    font-size: 18px; font-weight: 700; color: #0c2340;
  }
  .login-brand-row {
    display: flex; align-items: flex-start; gap: 10px;
    animation: login-rise 0.7s ease 0.1s both;
  }
  .login-brand-img {
    height: 40px; object-fit: contain;
  }
  .login-brand-tagline {
    font-size: 11px; color: #3d5166; font-weight: 600; line-height: 1.4; white-space: nowrap;
  }
  .login-form-title {
    font-family: 'DM Sans', -apple-system, sans-serif;
    font-size: 24px; font-weight: 800; color: #0c2340; margin: 0 0 4px 0;
    letter-spacing: -0.3px;
  }
  .login-form-title .accent {
    background: linear-gradient(90deg, #0c2340 0%, #1e3a8a 25%, #3b82f6 50%, #1e3a8a 75%, #0c2340 100%);
    background-size: 200% auto;
    -webkit-background-clip: text; background-clip: text;
    color: transparent;
    animation: login-shine 5s linear infinite;
  }
  .login-form-sub {
    font-size: 13px; color: rgba(12,35,64,0.55); margin: 0;
  }
  .login-form { display: flex; flex-direction: column; gap: 16px; animation: login-rise 0.7s ease 0.2s both; }
  .login-field { display: flex; flex-direction: column; gap: 6px; }
  .login-label { font-size: 12px; font-weight: 700; color: #0c2340; text-transform: uppercase; letter-spacing: 0.3px; }
  .login-input {
    padding: 12px 14px;
    border: 1px solid rgba(12,35,64,0.18);
    border-radius: 10px;
    font-size: 16px;
    width: 100%;
    background: rgba(255,255,255,0.7);
    backdrop-filter: blur(6px);
    color: #0c2340;
    transition: border-color 0.2s, box-shadow 0.2s;
    box-sizing: border-box;
    font-family: 'DM Sans', sans-serif;
  }
  .login-input:focus {
    outline: none;
    border-color: #1e3a8a;
    box-shadow: 0 0 0 3px rgba(30,58,138,0.12);
  }
  .login-hint { font-size: 11px; color: rgba(12,35,64,0.55); margin: 0; }
  .login-alert {
    background: #fef2f2; border: 1px solid rgba(185,28,28,0.2);
    color: #b91c1c; padding: 10px 14px;
    border-radius: 10px; font-size: 13px;
  }
  .login-error {
    background: #fef2f2; border: 1px solid rgba(185,28,28,0.2);
    color: #b91c1c; padding: 10px 14px;
    border-radius: 10px; font-size: 13px;
  }
  .login-btn {
    background: #0c2340;
    color: #fff; border: none;
    padding: 13px; border-radius: 10px;
    font-size: 16px; font-weight: 700; cursor: pointer;
    transition: filter 0.15s, transform 0.15s, box-shadow 0.15s;
    width: 100%;
    font-family: 'DM Sans', sans-serif;
  }
  .login-btn:hover:not(:disabled) {
    filter: brightness(1.12);
    transform: translateY(-1px);
    box-shadow:
      0 6px 20px rgba(12,35,64,0.22),
      0 0 0 1px rgba(30,58,138,0.12) inset;
  }
  .login-btn:disabled { opacity: 0.55; cursor: not-allowed; }
  .login-back-btn {
    background: none; border: none;
    color: #1e3a8a; font-size: 13px; font-weight: 600;
    cursor: pointer; text-align: center; padding: 4px;
    font-family: 'DM Sans', sans-serif;
    transition: color 0.2s;
  }
  .login-back-btn:hover { color: #0c2340; }
  .login-spinner {
    width: 14px; height: 14px; border-radius: 50%;
    border: 2px solid rgba(255,255,255,0.35);
    border-top: 2px solid #fff;
    display: inline-block;
    animation: loginSpin 0.7s linear infinite;
  }
  @media (max-width: 640px) {
    .login-page { padding: 20px; min-height: 100dvh; overflow-y: auto; }
    .login-card {
      flex-direction: column;
      min-height: unset;
      width: 100%;
      max-width: 70%;
    }
    .login-left {
      width: 100%;
      flex-shrink: 0;
    }
    .login-left img {
      width: 100%;
      height: auto;
      object-fit: unset;
    }
    .login-right { padding: 28px 24px; gap: 20px; }
    .login-mobile-header { display: none; }
    .login-form-title { font-size: 20px; }
  }
`;

// Generate random twinkling stars once (module-level for stability)
const LOGIN_STARS = Array.from({ length: 24 }, () => ({
  top: `${Math.random() * 100}%`,
  left: `${Math.random() * 100}%`,
  delay: `${Math.random() * 3.5}s`,
  size: 2 + Math.random() * 2,
}));

export default function Login() {
  const { authError } = useAuth();
  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const clearRecaptcha = () => {
    if (window.recaptchaVerifier) {
      try { window.recaptchaVerifier.clear(); } catch {}
      window.recaptchaVerifier = null;
    }
    const el = document.getElementById('recaptcha-container');
    if (el) el.innerHTML = '';
  };

  const setupRecaptcha = () => {
    clearRecaptcha();
    window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
      size: 'compact',
      callback: () => {},
      'expired-callback': clearRecaptcha,
    });
  };

  const normalizePhone = (raw) => {
    // Strip all spaces, dashes, parentheses
    let n = raw.replace(/[\s\-().]/g, '');
    // Already has + prefix
    if (n.startsWith('+')) return n;
    // Starts with 0091 (ISD prefix)
    if (n.startsWith('0091')) return '+91' + n.slice(4);
    // Starts with 91 followed by a 10-digit number → treat as +91XXXXXXXXXX
    if (/^91\d{10}$/.test(n)) return '+' + n;
    // Plain 10-digit number → prepend +91
    if (/^\d{10}$/.test(n)) return '+91' + n;
    // Fallback: prepend + so Firebase can at least try
    return '+' + n;
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');
    const normalized = normalizePhone(phone);
    const phoneRegex = /^\+[1-9]\d{7,14}$/;
    if (!phoneRegex.test(normalized)) {
      setError('Enter a valid 10-digit mobile number');
      return;
    }
    setLoading(true);
    try {
      setupRecaptcha();
      const result = await signInWithPhoneNumber(auth, normalized, window.recaptchaVerifier);
      setPhone(normalized);
      setConfirmationResult(result);
      setStep('otp');
    } catch (err) {
      clearRecaptcha();
      setError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    if (otp.length !== 6) { setError('Enter the 6-digit OTP'); return; }
    setLoading(true);
    try {
      await confirmationResult.confirm(otp);
      trackLogin('phone');
    } catch (err) {
      setError('Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{css}</style>
      <div className="login-page">
        {/* Animated background elements */}
        <div className="login-orb a" />
        <div className="login-orb b" />
        <div className="login-stars">
          {LOGIN_STARS.map((s, i) => (
            <span
              key={i}
              style={{
                top: s.top, left: s.left,
                width: s.size, height: s.size,
                animationDelay: s.delay,
              }}
            />
          ))}
        </div>

        <div className="login-card">
          {/* Left panel - hidden on mobile */}
          <div className="login-left">
            <img src="/nitin-potdar.jpg" alt="Nitin Potdar - AI Legal Career Advisor" />
          </div>

          {/* Right panel */}
          <div className="login-right">
            <div className="login-brand-row">
              <img src="/Ask Nitin Brand.png" alt="Ask Nitin" className="login-brand-img" />
              <span className="login-brand-tagline">Personal Mentor<br/>for Law Students</span>
            </div>
            <div>
              <h1 className="login-form-title">
                {step === 'phone' ? <><span className="accent">Sign in</span></> : <><span className="accent">Verify</span> your number</>}
              </h1>
              <p className="login-form-sub">
                {step === 'phone'
                  ? 'Enter your phone number to receive a one-time password'
                  : `OTP sent to ${phone}. Enter it below.`}
              </p>
            </div>

            {authError && <div className="login-alert">{authError}</div>}

            {step === 'phone' ? (
              <form onSubmit={handleSendOTP} className="login-form">
                <div className="login-field">
                  <label className="login-label">Phone Number</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+91 98765 43210"
                    className="login-input"
                    disabled={loading}
                    autoFocus
                  />
                </div>

                <div id="recaptcha-container" style={{ margin: '2px 0' }} />

                {error && <div className="login-error">{error}</div>}

                <button type="submit" disabled={loading} className="login-btn">
                  {loading ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                      <span className="login-spinner" /> Sending OTP...
                    </span>
                  ) : 'Send OTP'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOTP} className="login-form">
                <div className="login-field">
                  <label className="login-label">One-Time Password</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    placeholder="------"
                    className="login-input"
                    style={{ letterSpacing: '12px', fontSize: '24px', textAlign: 'center', fontWeight: '700' }}
                    disabled={loading}
                    autoFocus
                  />
                </div>

                {error && <div className="login-error">{error}</div>}

                <button type="submit" disabled={loading} className="login-btn">
                  {loading ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                      <span className="login-spinner" /> Verifying...
                    </span>
                  ) : 'Verify & Sign In'}
                </button>

                <button
                  type="button"
                  onClick={() => { setStep('phone'); setOtp(''); setError(''); clearRecaptcha(); }}
                  className="login-back-btn"
                >
                  ← Change number
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
